import { formatDateTime, formatDateTimeNoTimeZoneForReport, formatDate } from "@/src/date-helpers";
import {SaveStates} from "@/src/enums";
import { CoordinatorNote } from "../coordinator-note";
import { Donor } from "../donor";
import { FinalDisposition } from "../final-disposition";
import { MatchCalculatedStats } from "../match/calculated-stats";
import { Organ } from "../organ";
import { TransplantCenter } from "../transplant-center";
import { AcceptedTransplantDetails } from "../match/accepted-transplant-details";
import { ChecklistApplication } from "../match/checklist/checklist-application";


type BoolYNU = {
  value: boolean;
  display: string;
}
function mapFieldsDirectly<T>(fieldsToMap: Array<string>, from: any, to: T) {
  fieldsToMap.forEach((k) => {
    to[k] = from[k];
  });
}

function mapBooleanYesNo<T>(fieldsToMap: Array<string>, from: any, to: T) {
  fieldsToMap.forEach((k) => {
    const val = from[k];
    let display = '';
    if(val === true) {
      display = 'Yes'
    } else if(val === false) {
      display = 'No'
    } else {
      display = ''
    }

    to[k] = { value: val, display: display };
  });
}

function mapFormatDateTime<T>(fieldsToMap: Array<string>, from: any, to: T,  time_zone?: any) {
  fieldsToMap.forEach((k) => {
    const val = from[k];
    let display;
    if (!time_zone) {
      display = val ? formatDateTimeNoTimeZoneForReport(val) : '';
    } else {
      display = val ? formatDateTime(val) + ` (${time_zone})` : '';
    }
    

    to[k] = { value: val, display: display };
  });
  
}

function mapFormatDate<T>(fieldsToMap: Array<string>, from: any, to: T) {
  fieldsToMap.forEach((k) => {
    const val = from[k];
    const display = val ? formatDate(val) : '';
    

    to[k] = { value: val, display: display };
  });
  
}

function mapFieldsNA<T>(fieldsToMap: Array<string>, from: any, to: T) {
  fieldsToMap.forEach((k) => {
    to[k] = from[k] || 'N/A' ;
  });
}

function mapArrays<T>(fieldsToMap: Array<string>, from: any, to: T){
  fieldsToMap.forEach((k) => {
    const val = from[k];
    let display = '';
    if (Array.isArray(val)) {
      display = val.map(v => {
      if (Array.isArray(v)) {
        return v.map(i => i?.toString().trim()).filter(Boolean).join(', ');
      }
      return v?.toString().trim();
      }).filter(Boolean).join('; ');
    } else {
      display = '';
    }

    to[k] = display;
  });
}

export class InternalQARow {
  id: string;
  code: string;
  donor_id: string;
  organ_id: string;
  transplant_center_id: string;
  opo_id: string;
  offer_date: string
  decline_note: string;
  donor: Donor;
  transplantCenter: TransplantCenter;
  organ: Organ;
  calculated_stats: MatchCalculatedStats;
  coordinator_note: CoordinatorNote;
  auto_rule_out: boolean;
  case: boolean;
  physician: string;
  final_disposition: FinalDisposition;
  first_accepted_transplant_details: AcceptedTransplantDetails;
  second_accepted_transplant_details: AcceptedTransplantDetails;
  checklists!: ChecklistApplication[];

  get checklistStatus(): string {
    return (this.checklists || []).map(c => c.status_display).join('\n') || 'Not Started';
  }

  toJSON() {
    return { ...this, checklistStatus: this.checklistStatus };
  }

  dirty: boolean;
  current_state: string;
  final_disposition_approved: boolean;
  service_level_approved: boolean;
  errors: any;

  static fieldsToMap = {
    direct: [
      'id',
      'code',
      'transplant_center_id',
      'donor_id',
      'organ_id',
      'status_id',
      'extra_next_step_id',
      'decline_note',
      'physician',
      'email_url',
      'offer_date',
      'provisional_yes',
      'under_evaluation',
      'refused',
      'expedited',
      'donoredit_page_visited',
      'acceptancelist_page_visited',
      'matchlist_page_visited',
      'dismissed',
      'created_at',
      'updated_at',
      'case',
      'acceptancelist_page_visited_at',
      'matchlist_page_visited_at',
      'donoredit_page_visited_at',
      'latest_offer_email_at',
      'dismissed_at',
      'made_case_at',
      'revoked',
      'auto_rule_out',
      'final_disposition_id',
      'final_disposition_notes',
      'first_prov_yes_seq_any_tc',
      'second_prov_yes_seq_any_tc',
      'service_level_override',
      'latest_primary_backup_email_at',
      'sequence_status_page_visited',
      'sequence_status_page_visited_at',
      'bypass',
      'outcome_id',
      'physician_consulted',
      'add_manually',
      'created_by',
      'updated_by',
      'first_transplant_details',
      'second_transplant_details',
      'bypassed_offer',
      'derive_combined_recipients_physician',
      'derive_combined_recipients_notes',
      'final_disposition_approved_at',
      'service_level_approval_at'
    ],
    booleanYesNo: [
      'revoked',
      'expedited'
    ],
    directNA: [
    ],
    formatDateTime: [
      'offer_date'
    ],
    dates: [
    ],
    arrays: [
    ]
  }

  static buildFromAPI(record: any): InternalQARow {
    const row = new InternalQARow();
    record = record || {};

    mapFieldsDirectly<InternalQARow>(InternalQARow.fieldsToMap.direct, record, row);
    mapBooleanYesNo<InternalQARow>(InternalQARow.fieldsToMap.booleanYesNo, record, row);
    mapFieldsNA<InternalQARow>(InternalQARow.fieldsToMap.directNA, record, row);
    mapFormatDateTime<InternalQARow>(InternalQARow.fieldsToMap.formatDateTime, record, row);

    // mapArrays<InternalQARow>(InternalQARow.fieldsToMap.arrays, record, row);

    row.donor = Donor.buildFromAPI(record.donor);
    row.transplantCenter = TransplantCenter.buildFromAPI(record.transplant_center);
    row.organ = Organ.buildFromAPI(record.organ);
    row.calculated_stats = MatchCalculatedStats.buildFromAPI(record.calculated_stats);
    row.coordinator_note = CoordinatorNote.buildFromAPI(record.coordinator_note);
    row.final_disposition = FinalDisposition.buildFromAPI(record.final_disposition);
    row.first_accepted_transplant_details = AcceptedTransplantDetails.buildFromAPI(record.first_accepted_transplant_details);
    row.second_accepted_transplant_details = AcceptedTransplantDetails.buildFromAPI(record.second_accepted_transplant_details)

    row.checklists = [];
    if (record.checklists) {
      record.checklists.forEach((checklist: any) => {
        row.checklists.push(ChecklistApplication.buildFromAPI(checklist));
      });
    }

    row.dirty = false;
    row.final_disposition_approved = record.final_disposition_approved_at ? true : false;
    row.service_level_approved = record.service_level_approval_at ? true : false; 
    row.current_state = SaveStates.Blank;
    
    return row;
  }

  getRowClass(data) {
    if(!data) return '';

    if(data.current_state === SaveStates.Errored) return 'row-failed';
    if(data.current_state === SaveStates.Processing) return 'row-saving';
    if(data.current_state === SaveStates.Success) return 'row-saved';
    if(data.current_state === SaveStates.Idle) return '';
    if(data.dirty) return 'row-edited';
  }

  onModelValueUpdate(data, index, col, val) {
    this.setNestedValue(data, col.field, val); 
    if (col.field === 'auto_rule_out') {
      this.setPhysician(data, val);
    }
    data.dirty = true;
    data.current_state = SaveStates.Blank;
  }

  setPhysician(data, aro){
    if (aro === true && !data.physician) {
      this.setNestedValue(data, 'physician', 'Auto rule out');
    } else if (aro === false && data.physician === 'Auto rule out') {
      this.setNestedValue(data, 'physician', null);
    }
  }

  getNestedValue(obj,path) {
    return path.split('.').reduce((acc,part) => acc?.[part], obj)
  }

  setNestedValue(obj, path, value) {
    const keys = path.split('.')
    const last = keys.pop()
    const target = keys.reduce((acc,part) => acc[part] ??= {}, obj)
    target[last] = value
  }
}
